import type Geometry from 'ol/geom/Geometry';
import type { LayerModel } from '../core/types';
import type { LiveEffective } from './liveMeasure';
import { createSubareaPreview, logicalParentGeometry, type SubareaParent } from './operationalSubareas';
import { geodesicArea, measureArea } from './measurement';

/**
 * V1.9.3 H3 · the surface of a drawing that would really count inside its parent (Suerte or Tablón): the drawing cut
 * by the parent's ACTIVE geometry. It calls createSubareaPreview, the same function that builds the definitive preview
 * of an Área parcial and of «Dividir estado de Suerte», so the live figure and the final one cannot differ.
 * A drawing that does not touch the parent yet reads 0 ha effective; a sketch that cannot be cut yet (for instance
 * with crossing edges while it is being drawn) returns null and the card shows the drawn surface only.
 */
export function liveEffectiveFor(model: LayerModel, parent: SubareaParent): (drawn: Geometry) => LiveEffective | null {
  return (drawn) => {
    try {
      const preview = createSubareaPreview(model, parent, drawn);
      return { grossM2: preview.drawnAreaM2, effectiveM2: preview.effectiveAreaM2, outsideM2: preview.discardedAreaM2, parentM2: preview.parentGeometryAreaM2, restM2: Math.max(0, preview.parentGeometryAreaM2 - preview.effectiveAreaM2) };
    } catch (error) {
      if (error instanceof Error && /no intersecta/i.test(error.message)) {
        // Nothing of the drawing counts, so the whole parent remains: reconcile against its ACTIVE geometry.
        const gross = geodesicArea(drawn);
        const parentM2 = measureArea(logicalParentGeometry(model, parent)).areaM2;
        return { grossM2: gross, effectiveM2: 0, outsideM2: gross, parentM2, restM2: parentM2 };
      }
      return null;
    }
  };
}
